import React, { useState } from 'react';
import { X, Calendar, User, Package, FileText, Pencil, Trash2 } from 'lucide-react';
import EllipsisMenu from 'components/UI_components/EllipsisMenu';

const MaterialRequestSidebar = ({ request, onClose, onEditRequest, onDeleteRequest, isDeletingRequest = false }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
    const formattedTime = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    return `${formattedDate}, ${formattedTime}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'DRAFT':
        return 'bg-gray-100 text-gray-700';
      case 'SENT':
        return 'bg-blue-100 text-blue-700';
      case 'APPROVED':
        return 'bg-green-100 text-green-700';
      case 'REJECTED':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-end z-50">
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-xl">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{request.mr_number}</h2>
            <p className="text-sm text-gray-500 mt-1">{request.site_name}</p>
          </div>
          <div className="flex items-center gap-1">
            <EllipsisMenu
              items={[
                ...(request?.status === 'REJECTED'
                  ? []
                  : [
                    {
                      key: 'edit',
                      label: 'Edit',
                      icon: Pencil,
                      onClick: onEditRequest,
                      disabled: typeof onEditRequest !== 'function',
                    },
                    { type: 'separator' },
                  ]),
                {
                  key: 'delete',
                  label: isDeletingRequest ? 'Deleting...' : 'Delete',
                  icon: Trash2,
                  danger: true,
                  onClick: () => setShowDeleteConfirm(true),
                  disabled: isDeletingRequest || typeof onDeleteRequest !== 'function',
                },
              ]}
              buttonAriaLabel="Material request actions"
            />
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-6">
          {/* Status Section */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500 mb-1">Status</p>
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(request.status)}`}>
                  {request.status}
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Required By</p>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <p className="text-sm font-medium text-gray-900">{formatDate(request.due_date)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Creator & Approver Info */}
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <User className="w-5 h-5 text-gray-400 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-gray-500">Created by</p>
                <p className="text-sm font-medium text-gray-900">{request.created_by}</p>
                <p className="text-xs text-gray-400">{formatDateTime(request.created_on)}</p>
              </div>
            </div>

            {request.approved_by && (
              <div className="flex items-start gap-3">
                <User className="w-5 h-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-gray-500">Approved by</p>
                  <p className="text-sm font-medium text-gray-900">{request.approved_by}</p>
                </div>
              </div>
            )}

            {/* {request.last_updated_by && (
              <div className="flex items-start gap-3">
                <User className="w-5 h-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-gray-500">Last updated by</p>
                  <p className="text-sm font-medium text-gray-900">{request.last_updated_by}</p>
                  <p className="text-xs text-gray-400">{formatDateTime(request.last_updated_on)}</p>
                </div>
              </div>
            )} */}
          </div>

          {/* Comment Section */}
          {request.comment && (
            <div className="border-t border-gray-200 pt-4">
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-gray-500 mb-1">Comment</p>
                  <p className="text-sm text-gray-900">{request.comment}</p>
                </div>
              </div>
            </div>
          )}

          {/* Material Request Lines */}
          <div className="border-t border-gray-200 pt-4">
            <div className="flex items-center gap-2 mb-4">
              <Package className="w-5 h-5 text-gray-700" />
              <h3 className="text-lg font-semibold text-gray-900">
                Materials ({request.material_request_lines.length})
              </h3>
            </div>

            <div className="space-y-3">
              {request.material_request_lines.map((line, index) => (
                <div
                  key={line.id}
                  className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-sm transition-shadow"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <h4 className="text-base font-semibold text-gray-900">{line.material_name}</h4>
                      <p className="text-sm text-gray-500">{line.subcategory}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(line.status)}`}>
                      {line.status}
                    </span>
                  </div>

                  <div className="grid grid-cols-3 gap-3 mt-3">
                    <div>
                      <p className="text-xs text-gray-500">Quantity</p>
                      <p className="text-sm font-medium text-gray-900">
                        {line.quantity} {line.uom}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Brand</p>
                      <p className="text-sm font-medium text-gray-900">{line.brand_name}</p>
                    </div>
                    {line.specification && (
                      <div>
                        <p className="text-xs text-gray-500">Specification</p>
                        <p className="text-sm font-medium text-gray-900">{line.specification}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>

      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-sm rounded-xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <p className="text-base font-semibold text-gray-900">
                Delete Material Request
              </p>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="p-1 rounded-full hover:bg-gray-100 transition-colors"
                disabled={isDeletingRequest}
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            <div className="space-y-3 px-6 py-4 text-sm text-gray-700">
              <p>
                Are you sure you want to delete this material request? This action cannot be undone.
              </p>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-gray-200 bg-gray-50 px-6 py-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                disabled={isDeletingRequest}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  await onDeleteRequest?.();
                  setShowDeleteConfirm(false);
                }}
                className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 transition-colors disabled:opacity-70"
                disabled={isDeletingRequest}
              >
                {isDeletingRequest ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialRequestSidebar;
